/**
 * Digital SAT — reusable test player.
 *
 * Route: /dsat/attempt/:attempt_id
 *
 * Features per spec (Phase 1):
 *  - Timer (server-authoritative via remaining_seconds from autosave; survives refresh)
 *  - Warnings at 10/5/1 minutes remaining
 *  - Palette (answered / unanswered / flagged / current)
 *  - Flag for review
 *  - Prev / next / jump
 *  - Autosave answers, flags, elapsed
 *  - Desmos calculator button for Math (opens overlay)
 *  - Submit module ends timer; auto-submit on expiry
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Flag, Calculator, ChevronLeft, ChevronRight, Send, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

function formatTime(sec) {
  if (sec == null || sec < 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function DSATAttemptPage() {
  const { attempt_id } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState(new Set());
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [warned, setWarned] = useState({ 10: false, 5: false, 1: false });
  const [showCalc, setShowCalc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const startElapsedRef = useRef(0);
  const pageEnteredAtRef = useRef(Date.now());
  const isPreview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === '1';

  // Load attempt
  useEffect(() => {
    (async () => {
      try {
        const r = await ax.get(`/api/exam/v2/attempts/${attempt_id}`);
        setAttempt(r.data);
        setAnswers(r.data.answers || {});
        setFlags(new Set(r.data.flags || []));
        setIdx(r.data.current_index || 0);
        startElapsedRef.current = r.data.elapsed_seconds || 0;
        if (r.data.status === 'submitted') {
          nav(`/dsat/attempt/${attempt_id}/review`, { replace: true });
        }
      } catch (e) { setError(e.response?.data?.detail || 'Could not load attempt'); }
    })();
  }, [attempt_id, nav]);

  const questions = attempt?.questions_snapshot || [];
  const currentQ = questions[idx];
  const isMath = attempt?.exam_type === 'dsat' && (attempt?.section === 'math' || currentQ?.section === 'math');

  const setAnswer = useCallback((qid, val) => {
    setAnswers((prev) => ({ ...prev, [qid]: val }));
  }, []);

  const toggleFlag = useCallback((qid) => {
    setFlags((prev) => {
      const n = new Set(prev);
      if (n.has(qid)) n.delete(qid); else n.add(qid);
      return n;
    });
  }, []);

  // Autosave heartbeat
  useEffect(() => {
    if (!attempt || attempt.status !== 'in_progress') return undefined;
    const timer = setInterval(async () => {
      const elapsed = Math.floor((Date.now() - pageEnteredAtRef.current) / 1000) + startElapsedRef.current;
      try {
        const r = await ax.patch(`/api/exam/v2/attempts/${attempt_id}/autosave`, {
          answers, flags: Array.from(flags), elapsed_seconds: elapsed, current_index: idx,
        });
        if (typeof r.data?.remaining_seconds === 'number') setRemaining(r.data.remaining_seconds);
      } catch { /* silent; will retry */ }
    }, 8000);
    return () => clearInterval(timer);
  }, [attempt_id, attempt, answers, flags, idx]);

  // Local countdown between autosaves so the timer feels smooth
  useEffect(() => {
    if (remaining == null) return undefined;
    const t = setInterval(() => setRemaining((r) => (r == null ? null : Math.max(0, r - 1))), 1000);
    return () => clearInterval(t);
  }, [remaining]);

  // Initial deadline compute (before first autosave)
  useEffect(() => {
    if (!attempt?.server_deadline) return;
    const dl = new Date(attempt.server_deadline).getTime();
    setRemaining(Math.max(0, Math.floor((dl - Date.now()) / 1000)));
  }, [attempt?.server_deadline]);

  // Warnings + auto-submit
  useEffect(() => {
    if (remaining == null) return;
    if (!warned[10] && remaining <= 600 && remaining > 300) {
      toast.warning('10 minutes remaining');
      setWarned((w) => ({ ...w, 10: true }));
    }
    if (!warned[5] && remaining <= 300 && remaining > 60) {
      toast.warning('5 minutes remaining');
      setWarned((w) => ({ ...w, 5: true }));
    }
    if (!warned[1] && remaining <= 60 && remaining > 0) {
      toast.warning('1 minute remaining');
      setWarned((w) => ({ ...w, 1: true }));
    }
    if (remaining === 0 && attempt?.status === 'in_progress' && !submitting) {
      toast.error('Time up — submitting your attempt.');
      submitAttempt(true);
    }
  }, [remaining, warned, attempt?.status, submitting]);

  const submitAttempt = useCallback(async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const elapsed = Math.floor((Date.now() - pageEnteredAtRef.current) / 1000) + startElapsedRef.current;
      await ax.patch(`/api/exam/v2/attempts/${attempt_id}/autosave`, {
        answers, flags: Array.from(flags), elapsed_seconds: elapsed,
      });
      await ax.post(`/api/exam/v2/attempts/${attempt_id}/submit`);
      if (!auto) toast.success('Attempt submitted');
      nav(`/dsat/attempt/${attempt_id}/review`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); setSubmitting(false); }
  }, [attempt_id, answers, flags, submitting, nav]);

  const jump = (dir) => setIdx((i) => Math.max(0, Math.min(questions.length - 1, i + dir)));

  if (error) return <div className="max-w-lg mx-auto py-16 text-center text-red-700">{error}</div>;
  if (!attempt) return <div className="min-h-[60vh] flex items-center justify-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading attempt…</div>;

  const answeredCount = questions.filter((q) => answers[q.question_id] !== undefined).length;
  const isTimed = attempt.mode === 'timed';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dsat-test-player">
      {isPreview && (
        <div className="bg-amber-500 text-white text-center text-xs font-bold py-1.5 tracking-wide" data-testid="dsat-preview-banner">
          ADMIN PREVIEW — this attempt is not saved to student analytics.
        </div>
      )}
      {/* Sticky top bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
            <div className="text-xs text-gray-700 font-semibold">{attempt.test_title} · Question {idx + 1} of {questions.length}</div>
          </div>
          <div className="flex items-center gap-3">
            {isTimed && (
              <div className={`px-3 py-1 rounded font-mono text-lg font-bold ${remaining != null && remaining <= 300 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}
                data-testid="dsat-timer">
                {formatTime(remaining)}
              </div>
            )}
            {!isTimed && <Badge variant="outline" className="text-[10px]">Untimed Practice</Badge>}
            {isMath && (
              <Button size="sm" variant="outline" onClick={() => setShowCalc((v) => !v)} data-testid="dsat-toggle-calculator">
                <Calculator className="h-4 w-4 mr-1" /> Calculator
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr,240px] gap-6">
        {/* Question */}
        <Card className="p-6" data-testid={`dsat-question-${currentQ?.question_id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">
              {currentQ?.section === 'rw' ? 'Reading & Writing' : 'Math'} · {currentQ?.skill || currentQ?.domain}
            </div>
            <button
              onClick={() => toggleFlag(currentQ.question_id)}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${flags.has(currentQ?.question_id) ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid="dsat-flag-btn"
            >
              <Flag className="h-3 w-3" /> {flags.has(currentQ?.question_id) ? 'Flagged' : 'Flag'}
            </button>
          </div>

          {currentQ?.passage && (
            <div className="mt-4 p-4 rounded bg-gray-50 border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap">
              {currentQ.passage}
            </div>
          )}
          <div className="mt-4 text-base text-gray-900 font-medium whitespace-pre-wrap">
            {currentQ?.prompt}
          </div>

          {/* Answer options */}
          {currentQ?.question_type === 'mcq' && (
            <div className="mt-5 space-y-2" data-testid="dsat-options">
              {(currentQ.options || []).map((opt) => {
                const selected = answers[currentQ.question_id] === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setAnswer(currentQ.question_id, opt.key)}
                    className={`w-full text-left p-3 rounded border transition-colors ${selected ? 'border-red-700 bg-red-50' : 'border-gray-200 hover:border-gray-400'}`}
                    data-testid={`dsat-option-${opt.key}`}
                  >
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full mr-3 text-xs font-bold ${selected ? 'bg-red-700 text-white' : 'bg-gray-100 text-gray-700'}`}>{opt.key}</span>
                    <span className="text-sm text-gray-800">{opt.text}</span>
                  </button>
                );
              })}
            </div>
          )}
          {currentQ?.question_type === 'spr' && (
            <div className="mt-5">
              <Input
                value={answers[currentQ.question_id] ?? ''}
                onChange={(e) => setAnswer(currentQ.question_id, e.target.value)}
                placeholder="Enter your answer (numbers, fractions like 3/4, or decimals)"
                className="max-w-sm"
                data-testid="dsat-spr-input"
              />
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => jump(-1)} disabled={idx === 0} data-testid="dsat-prev">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <div className="text-xs text-gray-500">{answeredCount} of {questions.length} answered</div>
            {idx === questions.length - 1 ? (
              <Button size="sm" onClick={() => submitAttempt(false)} disabled={submitting} className="bg-green-700 hover:bg-green-800 text-white" data-testid="dsat-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Submit
              </Button>
            ) : (
              <Button size="sm" onClick={() => jump(1)} className="bg-red-700 hover:bg-red-800 text-white" data-testid="dsat-next">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </Card>

        {/* Palette */}
        <div className="lg:sticky lg:top-20 h-fit">
          <Card className="p-3">
            <div className="text-[10px] uppercase text-gray-500 font-bold tracking-wider mb-2">Question palette</div>
            <div className="grid grid-cols-6 lg:grid-cols-5 gap-1.5" data-testid="dsat-palette">
              {questions.map((q, i) => {
                const answered = answers[q.question_id] !== undefined;
                const flagged = flags.has(q.question_id);
                const active = i === idx;
                let style = 'bg-white border-gray-300 text-gray-700';
                if (answered) style = 'bg-red-100 border-red-300 text-red-800';
                if (flagged) style = 'bg-yellow-100 border-yellow-300 text-yellow-800';
                if (active) style += ' ring-2 ring-offset-1 ring-red-700';
                return (
                  <button
                    key={q.question_id}
                    onClick={() => setIdx(i)}
                    className={`h-8 rounded border text-[11px] font-mono ${style}`}
                    data-testid={`dsat-palette-${i}`}
                  >{i + 1}</button>
                );
              })}
            </div>
            <div className="mt-3 text-[10px] text-gray-500 space-y-0.5">
              <div><span className="inline-block w-2 h-2 rounded-full bg-red-400 mr-1"></span> Answered</div>
              <div><span className="inline-block w-2 h-2 rounded-full bg-yellow-400 mr-1"></span> Flagged</div>
              <div><span className="inline-block w-2 h-2 rounded-full bg-gray-300 mr-1"></span> Not answered</div>
            </div>
            <div className="mt-3">
              <Button size="sm" variant="outline" className="w-full" onClick={() => submitAttempt(false)} disabled={submitting} data-testid="dsat-submit-side">
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />} Submit attempt
              </Button>
            </div>
          </Card>
          {isTimed && remaining != null && remaining <= 300 && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 text-[11px] text-red-800 rounded flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> Time is running out. Answers autosave every 8s.
            </div>
          )}
        </div>
      </div>

      {/* Desmos calculator overlay */}
      {showCalc && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowCalc(false)} data-testid="dsat-calculator-overlay">
          <div className="bg-white rounded-lg w-full max-w-3xl p-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-2">
              <div className="text-xs font-semibold text-gray-700">Desmos Graphing Calculator (embedded, no network sync)</div>
              <button onClick={() => setShowCalc(false)} className="text-gray-500 hover:text-black" aria-label="Close calculator"><X className="h-4 w-4" /></button>
            </div>
            <iframe
              title="Desmos Calculator"
              src="https://www.desmos.com/calculator?embed"
              style={{ width: '100%', height: '520px', border: '0', borderRadius: '4px' }}
              data-testid="dsat-desmos-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
